import type { HistoryLabelOption } from '../types/history'

export const DEFAULT_ANNOTATION_BOX_COLOR = '#000000'

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i
const KNOWN_ANNOTATION_CLASS_COLORS: Record<string, string> = {
    aedes: '#52c41a',
    '伊蚊': '#52c41a',
    culex: '#2f54eb',
    '库蚊': '#2f54eb',
    '庫蚊': '#2f54eb',
    othermosquito: '#fa8c16',
    othermosquitoes: '#fa8c16',
    '其他蚊虫': '#fa8c16',
    '其它蚊虫': '#fa8c16',
    '其他蚊蟲': '#fa8c16',
    '其它蚊蟲': '#fa8c16',
}

export const normalizeAnnotationTypeKey = (value?: string | null): string =>
    typeof value === 'string' ? value.trim().toLowerCase() : ''

export const getLabelOptionIdentityKey = (
    option: HistoryLabelOption | undefined,
): string => {
    if (!option) {
        return ''
    }
    if (option.identity_key) {
        return normalizeAnnotationTypeKey(option.identity_key)
    }
    const modelKey = normalizeAnnotationTypeKey(option.model_key)
    const rawClass = normalizeAnnotationTypeKey(
        option.raw_class_norm || option.raw_class,
    )
    return modelKey && rawClass ? `${modelKey}:${rawClass}` : ''
}

export const getLabelOptionColorKey = (option: HistoryLabelOption): string =>
    getLabelOptionIdentityKey(option) ||
    normalizeAnnotationTypeKey(option.manual_type_key) ||
    normalizeAnnotationTypeKey(option.display_category_key) ||
    normalizeAnnotationTypeKey(option.eng_class) ||
    normalizeAnnotationTypeKey(option.class) ||
    `cls:${option.cls_id}`

export const getStableModelTypeColor = (identityKey: string): string => {
    void identityKey
    return DEFAULT_ANNOTATION_BOX_COLOR
}

export const getAnnotationClassColor = (classKey?: string | null): string => {
    const normalizedKey = normalizeAnnotationTypeKey(classKey)
    if (!normalizedKey) {
        return DEFAULT_ANNOTATION_BOX_COLOR
    }
    return (
        KNOWN_ANNOTATION_CLASS_COLORS[normalizedKey] ||
        KNOWN_ANNOTATION_CLASS_COLORS[classKey || ''] ||
        DEFAULT_ANNOTATION_BOX_COLOR
    )
}

export const buildLabelOptionColorMap = (
    labelOptions: HistoryLabelOption[],
): Map<string, string> => {
    const colors = new Map<string, string>()

    labelOptions.forEach((option) => {
        const optionKey = getLabelOptionColorKey(option)
        if (colors.has(optionKey)) {
            return
        }

        const rawIdentityKey = getLabelOptionIdentityKey(option)
        const explicitColor =
            option.color && HEX_COLOR_PATTERN.test(option.color)
                ? option.color.toLowerCase()
                : undefined
        const classKey =
            normalizeAnnotationTypeKey(option.display_category_key) ||
            normalizeAnnotationTypeKey(option.eng_class) ||
            normalizeAnnotationTypeKey(option.class)
        const color =
            explicitColor ||
            (rawIdentityKey
                ? getStableModelTypeColor(rawIdentityKey)
                : getAnnotationClassColor(classKey))

        colors.set(optionKey, color)
    })

    return colors
}

export const getLabelOptionColor = (
    option: HistoryLabelOption | undefined,
    labelOptionColors?: Map<string, string>,
): string => {
    if (option && labelOptionColors) {
        const mappedColor = labelOptionColors.get(
            getLabelOptionColorKey(option),
        )
        if (mappedColor) {
            return mappedColor
        }
    }

    if (option?.color && HEX_COLOR_PATTERN.test(option.color)) {
        return option.color.toLowerCase()
    }
    const rawIdentityKey = getLabelOptionIdentityKey(option)
    if (rawIdentityKey) {
        return getStableModelTypeColor(rawIdentityKey)
    }

    return getAnnotationClassColor(
        normalizeAnnotationTypeKey(option?.display_category_key) ||
            normalizeAnnotationTypeKey(option?.eng_class) ||
            normalizeAnnotationTypeKey(option?.class),
    )
}
